/**
 * Conference (web) -- the live call surface.
 *
 * Orchestrates the ported cfcalls hooks and renders a flat, id-keyed grid of
 * raw DOM <video> tiles positioned by tileGeometry (so tiles never unmount /
 * re-attach on participant-count changes). Media surfaces are plain DOM; the
 * surrounding chrome/controls use ALF.
 *
 * Web-only: relies on getUserMedia / RTCPeerConnection / <video srcObject>.
 */

import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {View} from 'react-native'
import {ReplaySubject} from 'rxjs'

import {convertCdnPreset} from '#/lib/media/util'
import {useProfileQuery, useProfilesQuery} from '#/state/queries/profile'
import {useSession} from '#/state/session'
import {useEnableMinimalShellMode} from '#/state/shell/minimal-mode'
import {atoms as a, useTheme, web} from '#/alf'
import {Button, ButtonText} from '#/components/Button'
import {Mute_Stroke2_Corner0_Rounded as MuteIcon} from '#/components/icons/Mute'
import {Text} from '#/components/Typography'
import {type CallFetch} from '../callFetch'
import {type CallSession} from '../callToken'
import {type PartyTracks} from '../hooks/partytracks'
import {usePeerConnection} from '../hooks/usePeerConnection'
import {usePulledTrack} from '../hooks/usePulledTrack'
import {type RoomUser, useRoomSocket} from '../hooks/useRoomSocket'
import {useUserMedia} from '../hooks/useUserMedia'
import {isPairRoomSlug, otherPairDid} from '../pairRoom'
import {computeTileRects, type TileRect} from '../tileGeometry'

const SELF_ID = '__self__'

export function Conference({
  roomSlug,
  session,
  callFetch,
  token,
  onLeave,
}: {
  roomSlug: string
  session: CallSession
  callFetch: CallFetch
  token: string | null
  onLeave: () => void
}) {
  const t = useTheme()
  const {stream, error: mediaError} = useUserMedia()
  const {partyTracks, sessionId, connectionState} = usePeerConnection({
    callFetch,
    token,
  })
  const {otherUsers, status, joinError, sendUserUpdate} = useRoomSocket({
    roomSlug,
    token,
    selfUserId: session.did,
    selfName: session.displayName,
  })
  const isMutualRoom = roomSlug.startsWith('m-')
  const isPairRoom = isPairRoomSlug(roomSlug)
  // The peer we're calling in a 1:1 room (for the "Ringing…" label while alone).
  const peerDid = isPairRoom ? otherPairDid(roomSlug, session.did) : undefined
  const {data: peerProfiles} = useProfilesQuery({
    handles: peerDid ? [peerDid] : [],
  })
  const peerProfile = peerProfiles?.profiles?.[0]
  const peerLabel =
    peerProfile?.displayName || peerProfile?.handle || 'them'
  // While alone in a pair room, we're ringing the other participant.
  const isRinging =
    isPairRoom && status === 'connected' && otherUsers.length === 0

  // Local avatar: a real logged-in user's atproto profile picture, broadcast to
  // peers so their camera-off tile shows it. Guests have no atproto profile ->
  // undefined -> peers/self fall back to initials. The query is a no-op for
  // guests (enabled:!!did, and we pass undefined).
  //
  // Use the full `avatar` preset (not the small `avatar_thumbnail`): call tiles
  // render large (up to ~200px), where the thumbnail would upscale and blur.
  // Mirrors UserAvatar's rule (thumbnail only when size < 90).
  const {currentAccount} = useSession()
  const profile = useProfileQuery({
    did: session.isGuest ? undefined : currentAccount?.did,
  })
  const localAvatarUrl =
    session.isGuest || !profile.data?.avatar
      ? undefined
      : convertCdnPreset(profile.data.avatar, 'avatar')

  // Take over the screen: collapse the shell and hard-hide the persistent web
  // chrome while in a call. The nav rails live in a separate stacking context
  // (mounted at the navigator level), so z-index can't win against them -- we
  // suppress them by CSS instead, mirroring batesky's RoomCallScreen. The
  // selectors match DesktopLeftNav (role="navigation") and DesktopRightNav /
  // the sticky Cam button (position:fixed + left:50% / bottom:*). Removed on
  // unmount so the chrome returns when the call ends.
  useEnableMinimalShellMode({enabled: true})
  useEffect(() => {
    const style = document.createElement('style')
    style.id = 'bate-call-hide-chrome'
    style.innerHTML = `
      [role="navigation"],
      [style*="position: fixed"][style*="left: 50%"],
      [style*="position: fixed"][style*="bottom: 0"] {
        display: none !important;
        opacity: 0 !important;
        pointer-events: none !important;
        visibility: hidden !important;
      }
    `
    document.head.appendChild(style)
    return () => {
      document.getElementById('bate-call-hide-chrome')?.remove()
    }
  }, [])

  const [micEnabled, setMicEnabled] = useState(true)
  const [camEnabled, setCamEnabled] = useState(true)
  const [publishedTracks, setPublishedTracks] = useState<{
    audio?: string
    video?: string
  }>({})

  const audioTrack = stream ? stream.getAudioTracks()[0] || null : null
  const videoTrack = stream ? stream.getVideoTracks()[0] || null : null

  // Apply mute/camera toggles to the live tracks. The track is read from
  // `stream` INSIDE the effect (not closed over as a render-local) so we're
  // toggling a property on an external WebRTC object, not mutating React state.
  useEffect(() => {
    const track = stream?.getAudioTracks()[0]
    if (track) track.enabled = micEnabled
  }, [stream, micEnabled])
  useEffect(() => {
    const track = stream?.getVideoTracks()[0]
    if (track) track.enabled = camEnabled
  }, [stream, camEnabled])

  // Push our local tracks to the SFU (inline; mirrors electron PushLocalTracks).
  usePushLocalTracks({
    partyTracks,
    sessionId,
    audioTrack,
    videoTrack,
    onTracksPushed: setPublishedTracks,
  })

  // Re-announce our presence whenever our tracks / toggles / avatar change.
  useEffect(() => {
    sendUserUpdate({
      tracks: publishedTracks,
      micEnabled,
      videoEnabled: camEnabled,
      avatarUrl: localAvatarUrl,
    })
  }, [publishedTracks, micEnabled, camEnabled, localAvatarUrl, sendUserUpdate])

  const onToggleMic = useCallback(() => setMicEnabled(v => !v), [])
  const onToggleCam = useCallback(() => setCamEnabled(v => !v), [])

  // Tile geometry over a measured container.
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [size, setSize] = useState({width: 0, height: 0})
  useEffect(() => {
    const el = containerRef.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(entries => {
      const r = entries[0]?.contentRect
      if (r) setSize({width: r.width, height: r.height})
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const visibleIds = useMemo(
    () => [SELF_ID, ...otherUsers.map(u => u.id)],
    [otherUsers],
  )
  const rects = useMemo(
    () =>
      computeTileRects({visibleIds, width: size.width, height: size.height}),
    [visibleIds, size.width, size.height],
  )

  return (
    // Full-bleed call surface (batesky RoomCallScreen container values). The
    // chrome is hidden by the injected CSS above; this just guarantees the call
    // fills the viewport and gives the tile ResizeObserver a real size.
    <div
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        background: '#000',
        overflow: 'hidden',
      }}>
      {/* Tile surface (raw DOM for absolute positioning + <video>). */}
      <div
        ref={containerRef}
        style={{position: 'relative', flex: 1, minHeight: 0}}>
        <SelfTile
          stream={stream}
          rect={rects.get(SELF_ID)}
          muted={!micEnabled}
          cameraOff={!camEnabled}
          avatarUrl={localAvatarUrl}
        />
        {otherUsers.map(user => (
          <RemoteTile
            key={user.id}
            user={user}
            partyTracks={partyTracks}
            rect={rects.get(user.id)}
          />
        ))}
      </div>

      {/* Controls + status row, above the video surface, in the flex column. */}
      <View
        style={[
          a.flex_row,
          a.gap_md,
          a.p_md,
          a.justify_center,
          a.align_center,
          web({zIndex: 10}),
        ]}>
        <Button
          label={micEnabled ? 'Mute microphone' : 'Unmute microphone'}
          onPress={onToggleMic}
          color={micEnabled ? 'secondary' : 'primary'}
          size="small">
          <ButtonText>{micEnabled ? 'Mute' : 'Unmute'}</ButtonText>
        </Button>
        <Button
          label={camEnabled ? 'Turn camera off' : 'Turn camera on'}
          onPress={onToggleCam}
          color={camEnabled ? 'secondary' : 'primary'}
          size="small">
          <ButtonText>{camEnabled ? 'Camera off' : 'Camera on'}</ButtonText>
        </Button>
        <Button
          label="Leave call"
          onPress={onLeave}
          color="negative"
          size="small">
          <ButtonText>Leave</ButtonText>
        </Button>
      </View>

      {isRinging && !mediaError && !joinError && (
        <View
          style={[
            a.absolute,
            a.align_center,
            {top: '45%', left: 0, right: 0},
            web({zIndex: 10}),
          ]}>
          <Text style={[a.text_lg, a.font_bold, {color: t.palette.white}]} emoji>
            Ringing {peerLabel}…
          </Text>
        </View>
      )}

      {(status !== 'connected' ||
        connectionState !== 'connected' ||
        mediaError ||
        joinError) && (
        <View style={[a.absolute, {top: 12, left: 12}, web({zIndex: 10})]}>
          <Text style={[a.text_sm, {color: t.palette.white}]}>
            {joinError
              ? isMutualRoom
                ? 'You can only join this room if you and the owner follow each other.'
                : isPairRoom
                  ? 'You can’t join this 1:1 call.'
                  : 'Couldn’t join this room.'
              : mediaError
                ? `Camera/mic unavailable: ${mediaError.message}`
                : status === 'reconnecting'
                  ? 'Reconnecting…'
                  : status !== 'connected'
                    ? 'Connecting to room…'
                    : `Negotiating media… (${connectionState})`}
          </Text>
        </View>
      )}
    </div>
  )
}

/** Pushes local audio+video to the SFU via stable ReplaySubjects. */
function usePushLocalTracks({
  partyTracks,
  sessionId,
  audioTrack,
  videoTrack,
  onTracksPushed,
}: {
  partyTracks: PartyTracks | null
  sessionId: string | null
  audioTrack: MediaStreamTrack | null
  videoTrack: MediaStreamTrack | null
  onTracksPushed: (t: {audio?: string; video?: string}) => void
}) {
  // Stable ReplaySubject per kind for the component's lifetime. ReplaySubject(1)
  // (not Subject) so a track fed before the push subscription attaches is
  // replayed -- otherwise the SFU never learns the track and peers see a black
  // tile.
  const audioSubject = useMemo(() => new ReplaySubject<MediaStreamTrack>(1), [])
  const videoSubject = useMemo(() => new ReplaySubject<MediaStreamTrack>(1), [])

  const pushed = useRef<{audio?: string; video?: string}>({})
  const sessionIdRef = useRef(sessionId)
  useEffect(() => {
    sessionIdRef.current = sessionId
  }, [sessionId])

  useEffect(() => {
    if (!partyTracks) return
    const aSub = partyTracks
      .push(audioSubject)
      .subscribe((meta: {trackName?: string; sessionId?: string}) => {
        if (!meta?.trackName) return
        pushed.current.audio = `${meta.sessionId || sessionIdRef.current}/${meta.trackName}`
        onTracksPushed({...pushed.current})
      })
    const vSub = partyTracks
      .push(videoSubject)
      .subscribe((meta: {trackName?: string; sessionId?: string}) => {
        if (!meta?.trackName) return
        pushed.current.video = `${meta.sessionId || sessionIdRef.current}/${meta.trackName}`
        onTracksPushed({...pushed.current})
      })
    return () => {
      aSub.unsubscribe()
      vSub.unsubscribe()
    }
  }, [partyTracks, onTracksPushed, audioSubject, videoSubject])

  useEffect(() => {
    if (audioTrack) audioSubject.next(audioTrack)
  }, [audioTrack, audioSubject])
  useEffect(() => {
    if (videoTrack) videoSubject.next(videoTrack)
  }, [videoTrack, videoSubject])
}

function SelfTile({
  stream,
  rect,
  muted,
  cameraOff,
  avatarUrl,
}: {
  stream: MediaStream | null
  rect?: TileRect
  muted: boolean
  cameraOff: boolean
  avatarUrl?: string
}) {
  const ref = useRef<HTMLVideoElement | null>(null)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    el.srcObject = stream
    if (stream) void el.play().catch(() => {})
  }, [stream])
  // Camera off (or no stream yet) -> show the avatar fallback and hide the
  // (now black/frozen) video element so it doesn't cover it.
  const showVideo = Boolean(stream) && !cameraOff
  return (
    <TileFrame
      rect={rect}
      label="You"
      hidden={!showVideo}
      muted={muted}
      avatarUrl={avatarUrl}>
      <video
        ref={ref}
        autoPlay
        playsInline
        muted
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          transform: 'scaleX(-1)',
          display: showVideo ? 'block' : 'none',
        }}
      />
    </TileFrame>
  )
}

function RemoteTile({
  user,
  partyTracks,
  rect,
}: {
  user: RoomUser
  partyTracks: PartyTracks | null
  rect?: TileRect
}) {
  const videoTrack = usePulledTrack(partyTracks, user.tracks?.video)
  const audioTrack = usePulledTrack(partyTracks, user.tracks?.audio)
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const audioRef = useRef<HTMLAudioElement | null>(null)

  useEffect(() => {
    const el = videoRef.current
    if (!el) return
    el.srcObject = videoTrack ? new MediaStream([videoTrack]) : null
    if (videoTrack) void el.play().catch(() => {})
  }, [videoTrack])

  useEffect(() => {
    const el = audioRef.current
    if (!el) return
    el.srcObject = audioTrack ? new MediaStream([audioTrack]) : null
    if (audioTrack) void el.play().catch(() => armAudioGestureRetry(el))
  }, [audioTrack])

  const showVideo = Boolean(videoTrack) && user.videoEnabled !== false

  return (
    <TileFrame
      rect={rect}
      label={user.name || 'Guest'}
      hidden={!showVideo}
      muted={user.micEnabled === false}
      avatarUrl={user.avatarUrl}>
      <video
        ref={videoRef}
        autoPlay
        playsInline
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          display: showVideo ? 'block' : 'none',
        }}
      />
      <audio ref={audioRef} autoPlay />
    </TileFrame>
  )
}

/** Absolute-positioned tile frame with a name label + camera-off fallback. */
function TileFrame({
  rect,
  label,
  hidden,
  muted = false,
  avatarUrl,
  children,
}: {
  rect?: TileRect
  label: string
  hidden: boolean
  /** Show a muted-mic icon next to the name when true. */
  muted?: boolean
  /** Profile picture to show on camera-off; falls back to initials. */
  avatarUrl?: string
  children: React.ReactNode
}) {
  const style: React.CSSProperties = rect
    ? {
        position: 'absolute',
        left: rect.x,
        top: rect.y,
        width: rect.width,
        height: rect.height,
        borderRadius: 12,
        overflow: 'hidden',
        background: '#111',
        display: 'block',
      }
    : {position: 'absolute', width: 0, height: 0, overflow: 'hidden'}

  // Centered circular avatar/initials shown when the camera is off (electron's
  // AvatarFallback sizing: max(40, min(h/2, w-30, 200))).
  const circle = rect
    ? Math.max(40, Math.min(rect.height / 2, rect.width - 30, 200))
    : 96
  const initials = (label || '').slice(0, 2).toUpperCase() || '?'

  return (
    <div style={style}>
      {children}
      {hidden && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt=""
              referrerPolicy="no-referrer"
              width={circle}
              height={circle}
              style={{
                width: circle,
                height: circle,
                borderRadius: '50%',
                objectFit: 'cover',
              }}
            />
          ) : (
            <div
              style={{
                width: circle,
                height: circle,
                borderRadius: '50%',
                background: '#333',
                color: '#ccc',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: Math.round(circle * 0.4),
                fontWeight: 600,
              }}>
              {initials}
            </div>
          )}
        </div>
      )}
      <div
        style={{
          position: 'absolute',
          left: 8,
          bottom: 8,
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          padding: '2px 8px',
          borderRadius: 6,
          background: 'rgba(0,0,0,0.5)',
          color: '#fff',
          fontSize: 12,
        }}>
        {muted && <MuteIcon width={13} fill="#fff" />}
        {label}
      </div>
    </div>
  )
}

// --- autoplay recovery (shared, one-shot gesture retry) --------------------
const pendingAudioEls = new Set<HTMLMediaElement>()
let gestureArmed = false
function retryPendingAudio() {
  gestureArmed = false
  document.removeEventListener('click', retryPendingAudio)
  document.removeEventListener('keydown', retryPendingAudio)
  for (const el of pendingAudioEls) void el.play().catch(() => {})
  pendingAudioEls.clear()
}
function armAudioGestureRetry(el: HTMLMediaElement) {
  pendingAudioEls.add(el)
  if (gestureArmed) return
  gestureArmed = true
  document.addEventListener('click', retryPendingAudio, {once: true})
  document.addEventListener('keydown', retryPendingAudio, {once: true})
}
